import React, { useEffect, useState } from 'react';
import './index.css';
import { DEFAULT_SITE_NAVIGATION, fetchSiteNavigation, homeNavigationItems } from '../../siteNavigation';
import { PublicPage } from '../../components/PublicShell';

const conferenceImage = require('../../imgs/agist-template/05-cpsi-2027-tokyo-skyline.webp');
const speakerImage = require('../../imgs/agist-template/03-community-scholar.webp');
const attendeeImage = require('../../imgs/agist-template/04-news-attendee.webp');

const programItems = [
  {
    number: '01',
    title: 'Global Conferences',
    body: 'A rigorous international forum for presenting emerging research in cyber-physical-social intelligence.',
    action: 'Explore the current conference',
    href: '',
  },
  {
    number: '02',
    title: 'Workshops & Training',
    body: 'Focused schools and training programs that connect foundational thinking with current research practice.',
    action: 'View academic programs',
    href: '#/home#training',
  },
  {
    number: '03',
    title: 'Scholarly Publications',
    body: 'Publication venues supporting interdisciplinary work across intelligent systems, command, and control.',
    action: 'Visit the journal',
    href: 'http://cpsi.agist.org',
  },
];

const principleItems = [
  {
    number: '01',
    title: 'Interdisciplinary by design',
    body: 'Connecting computation, engineering, social systems, and human-centered inquiry.',
  },
  {
    number: '02',
    title: 'International in scope',
    body: 'Creating meaningful scholarly exchange across institutions, regions, and career stages.',
  },
  {
    number: '03',
    title: 'Rigorous in practice',
    body: 'Supporting thoughtful review, open discussion, and research with lasting academic value.',
  },
];

const trainingDescriptions = {
  '2023': 'An intensive academic program connecting emerging researchers with current CPSI methods and questions.',
  '2022': 'Focused lectures, scholarly discussion, and interdisciplinary training for the next generation of researchers.',
  '2021': 'A structured learning forum exploring foundations and new directions in cyber-physical-social intelligence.',
};

function externalArrow() {
  return <span aria-hidden="true">{'\u2197'}</span>;
}

function renderTextBody(body) {
  if (!body) return null;
  if (/<[a-z][\s\S]*>/i.test(body)) {
    return <div dangerouslySetInnerHTML={{ __html: body }} />;
  }
  return body.split(/\n+/).filter(Boolean).map((line) => <p key={line}>{line}</p>);
}

function renderActionLink(href, label, className = 'agist-template-link') {
  if (!href) return null;
  const content = (
    <>
      <span>{label}</span>
      {externalArrow()}
    </>
  );
  return <a className={className} href={href}>{content}</a>;
}

function yearFromLabel(label) {
  const match = String(label || '').match(/(20\d{2})/);
  return match ? match[1] : '';
}

export default function Home() {
  const [homeNews, setHomeNews] = useState([]);
  const [navigation, setNavigation] = useState(DEFAULT_SITE_NAVIGATION);

  useEffect(() => {
    let cancelled = false;
    fetch('./api/home_news.php', { cache: 'no-store' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('home news api failed'))))
      .then((data) => {
        if (!cancelled && Array.isArray(data.rows) && data.rows.length > 0) {
          setHomeNews(data.rows.slice(0, 1));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSiteNavigation()
      .then((data) => {
        if (!cancelled) setNavigation(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const conferenceLinks = [
    ...homeNavigationItems(navigation.current_conferences),
    ...homeNavigationItems(navigation.past_conferences),
  ];
  const workshopLinks = homeNavigationItems(navigation.workshops);
  const publicationLinks = homeNavigationItems(navigation.publications);
  const featuredNews = homeNews[0] || {
    title: 'ICCSI renames to CPSI',
    content:
      'Effective January 1, 2025, the International Conference on Cyber-physical Social Intelligence adopts the acronym CPSI. The full conference title, scope, committees, review standards, and scholarly community remain unchanged.\nArchival references remain ICCSI 2021-ICCSI 2024; editions from 2025 onward use CPSI.',
    cover_image: '',
  };
  const featuredConference = conferenceLinks[0] || DEFAULT_SITE_NAVIGATION.current_conferences[0];
  const featuredWorkshop = workshopLinks[0] || DEFAULT_SITE_NAVIGATION.workshops[0];
  const featuredConferenceYear = yearFromLabel(featuredConference.label);

  return (
    <PublicPage className="agist-home">
      <section className="hero" id="top">
        <div className="hero-image" aria-hidden="true"></div>
        <div className="hero-grid" aria-hidden="true"></div>
        <div className="shell hero-content">
          <div className="hero-copy">
            <p className="eyebrow light">Global scholarship. Shared intelligence.</p>
            <h1>Advancing intelligent science through global scholarly exchange.</h1>
            <p className="hero-lead">
              AGIST connects researchers, educators, and practitioners to shape the future of intelligent science and technology.
            </p>
            <div className="hero-actions">
              <a className="button button-gold" href={featuredConference.url}>Discover {featuredConference.label} {externalArrow()}</a>
              <a className="text-link light-link" href="#about">Learn about AGIST <span aria-hidden="true">{'\u2193'}</span></a>
            </div>
          </div>
          <aside className="hero-note" aria-label="AGIST research focus">
            <p className="note-label">Our shared focus</p>
            <p className="note-title">Cyber-Physical-Social Intelligence</p>
            <p>Building an international community around the systems, methods, and human questions shaping intelligent societies.</p>
            <div className="note-rule"></div>
            <div className="note-meta"><span>Research</span><span>Education</span><span>Exchange</span></div>
          </aside>
        </div>
      </section>

      <a className="announcement" href="#news">
        <div className="shell announcement-inner">
          <span className="announcement-tag">Institutional update</span>
          <p>ICCSI is now CPSI - the conference acronym changed effective January 1, 2025.</p>
          <span className="announcement-arrow" aria-hidden="true">{'\u2192'}</span>
        </div>
      </a>

      <section className="section about-section" id="about">
        <div className="shell split-heading">
          <div>
            <p className="eyebrow">About the association</p>
            <h2>A scholarly network for ideas that cross boundaries.</h2>
          </div>
          <div className="body-copy">
            <p>The Association of Global Intelligent Science and Technology is a technical professional organization committed to advancing technology for the betterment of humanity.</p>
            <p>
              We create enduring connections between disciplines, institutions, and regions through conferences, education, and publication.
            </p>
            {renderActionLink('#/about', 'Read about AGIST', 'text-link')}
          </div>
        </div>
        <div className="shell principles-grid">
          {principleItems.map((item) => (
            <article key={item.number}>
              <span>{item.number}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section programs-section" id="programs">
        <div className="shell section-heading-row">
          <div>
            <p className="eyebrow">Academic programs</p>
            <h2>How the community moves knowledge forward.</h2>
          </div>
          <p>From the conference hall to the journal page, AGIST creates structured spaces for research to be challenged, refined, and shared.</p>
        </div>
        <div className="shell program-list">
          {programItems.map((item) => (
            <article key={item.number} className="program-row">
              <span className="program-number">{item.number}</span>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
              <a href={item.number === '01' ? featuredConference.url : item.number === '02' ? featuredWorkshop.url : item.href}>
                {item.number === '01' ? `Explore ${featuredConference.label}` : item.action} {externalArrow()}
              </a>
            </article>
          ))}
        </div>
      </section>

      <section className="section training-section" id="training">
        <div className="shell training-layout">
          <div className="training-intro">
            <p className="eyebrow">Workshops and training</p>
            <h2>Focused learning for emerging research communities.</h2>
            <p>
              AGIST schools complement the conference program with concentrated study, discussion, and direct exchange between established scholars and early-career researchers.
            </p>
          </div>
          <div className="training-list">
              {workshopLinks.map((item) => {
                const year = yearFromLabel(item.label);
                return (
                <a href={item.url} key={`${item.label}-${item.url}`} className="training-item">
                    <span className="training-year">{year}</span>
                    <div>
                    <h3>{item.label.replace(year, '').trim()}</h3>
                      <p>{trainingDescriptions[year] || 'A focused AGIST academic program for scholarly training and exchange.'}</p>
                    </div>
                    {externalArrow()}
                  </a>
                );
              })}
            </div>
        </div>
      </section>

      <section className="conference-section" id="conference">
        <div className="shell conference-grid">
          <div className="conference-image-wrap">
            <img src={conferenceImage} alt={`${featuredConference.label} conference venue`} />
            <div className="image-caption"><span>International scholarly exchange</span><span>AGIST Conference Series</span></div>
          </div>
          <div className="conference-copy">
            <p className="eyebrow light">Featured conference</p>
            <h2>{featuredConference.label}</h2>
            <p className="conference-name">International Conference on Cyber-Physical-Social Intelligence</p>
            <p>Join an international community exploring the theories, technologies, and societal implications of deeply connected intelligent systems.</p>
            <a className="button button-outline" href={featuredConference.url}>Visit the conference site {externalArrow()}</a>
            <div className="edition-list" aria-label="Previous conference editions">
            {conferenceLinks.map((item) => (
              <a href={item.url} key={`${item.label}-${item.url}`}>
                <span>{yearFromLabel(item.label)}</span>
                <strong>{item.label}</strong>
                {externalArrow()}
              </a>
            ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section publications-section" id="publications">
        <div className="shell publication-layout">
          <div>
            <p className="eyebrow">Publications</p>
            <h2>Research deserves a serious home.</h2>
          </div>
          <div className="journal-stack">
            {publicationLinks.map((item, index) => (
              <a className="journal-card" href={item.url} key={`${item.label}-${item.url}`}>
                <div>
                  <span className="journal-type">{index === 0 ? 'AGIST Journal' : 'Partner publication'}</span>
                  <h3>{item.label}</h3>
                </div>
                <span aria-hidden="true">{'\u2197'}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="community-section" id="community">
        <div className="shell community-heading">
          <div>
            <p className="eyebrow">A community of inquiry</p>
            <h2>Knowledge grows through conversation.</h2>
          </div>
          <p>AGIST brings together established scholars, emerging researchers, students, and practitioners in a setting built for serious exchange.</p>
        </div>
        <div className="shell photo-grid">
          <figure className="photo-main"><img src={speakerImage} alt="A scholar speaking during an AGIST conference discussion" /></figure>
          <figure><img src={attendeeImage} alt="An attendee listening during an AGIST conference session" /></figure>
          <div className="photo-quote">
            <span aria-hidden="true">{'\u201c'}</span>
            <blockquote>Progress begins when disciplines meet, questions deepen, and ideas are shared with intellectual generosity.</blockquote>
          </div>
        </div>
      </section>

      <section className="section news-section" id="news">
        <div className="shell news-layout">
          <div className="news-heading">
            <p className="eyebrow">News and announcements</p>
            <h2>Updates from the AGIST community.</h2>
          </div>
          <div className="news-list">
          <article className="news-feature">
              <div className="news-meta"><span>Institutional update</span><time dateTime="2025-01-01">January 1, 2025</time></div>
              <h3>{featuredNews.title}</h3>
              {renderTextBody(featuredNews.content || featuredNews.summary)}
              {featuredNews.detail_url && renderActionLink(featuredNews.detail_url.startsWith('#/') ? featuredNews.detail_url : `#${featuredNews.detail_url}`, 'Read more')}
          </article>
            <a className="news-brief" href={featuredConference.url}>
              <div className="news-meta"><span>Conference series</span><span>{featuredConferenceYear}</span></div>
              <h3>{featuredConference.label}</h3>
              <p>Discover the next edition of AGIST's international conference series.</p>
              <span className="news-link">Open conference site {externalArrow()}</span>
            </a>
          </div>
        </div>
      </section>

      <section className="membership-section" id="membership">
        <div className="shell membership-inner">
          <div>
            <p className="eyebrow light">Membership</p>
            <h2>Take part in a global community shaping intelligent science.</h2>
          </div>
          <div>
            <p>
              Connect with peers, contribute to scholarly programs, and help strengthen the exchange of ideas across borders and disciplines.
            </p>
            <a className="button button-gold" href="#/membership">Become an AGIST member {externalArrow()}</a>
          </div>
        </div>
      </section>

      <section className="contacts-section" id="contacts">
        <div className="shell contacts-grid">
          <div>
            <p className="eyebrow">Contacts</p>
            <h2>Start a scholarly conversation.</h2>
          </div>
          <div className="contact-details">
            <a href="mailto:info@agist.org"><span>Email</span><strong>info@agist.org</strong>{externalArrow()}</a>
            <a href="https://twitter.com/DaoAgist"><span>Social</span><strong>@DaoAgist</strong>{externalArrow()}</a>
            <a href="#/membership"><span>Community</span><strong>Membership enquiries</strong>{externalArrow()}</a>
          </div>
        </div>
      </section>
    </PublicPage>
  );
}
